import React, { useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { Mixpanel } from '../../../../config/Mixpanel'
import constants from '../../../../utilities/constants'

const CancellationModal = ({ isOpen, setIsOpen, userName = 'Iram', onContinue, timeLeft, isEvenUser, isPlacementAddonSelected, discountedPriceEvenUser, isOneTime, razorpayHandlerEvenUser, discountedPriceOneRupee, razorpayHandler, discountedPriceOddUser, isProAddonSelected }) => {

  // Track modal open/close
  useEffect(() => {
    if (isOpen) {
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_CANCELLATION_MODAL_OPENED, {
        user_name: userName,
        is_even_user: isEvenUser,
        is_addon_selected: isPlacementAddonSelected,
        is_one_time: isOneTime,
        current_timer_seconds: timeLeft,
        timestamp: new Date().toISOString()
      })
    }
  }, [isOpen, userName, isEvenUser, isPlacementAddonSelected, isOneTime, timeLeft])

  // Track timer expired when timeLeft reaches 0
  useEffect(() => {
    if (isOpen && timeLeft === 0) {
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_CANCELLATION_MODAL_TIMER_EXPIRED, {
        user_name: userName,
        is_even_user: isEvenUser,
        timestamp: new Date().toISOString()
      })
    }
  }, [timeLeft, isOpen, userName, isEvenUser])


  // Format time to display as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}M : ${remainingSeconds.toString().padStart(2, '0')}S`
  }

  const closeModal = () => {
    // Track modal close event
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_CANCELLATION_MODAL_CLOSED, {
      user_name: userName,
      is_even_user: isEvenUser,
      remaining_timer_seconds: timeLeft,
      close_method: 'close_button',
      timestamp: new Date().toISOString()
    })
    setIsOpen(false)
  }

  const handleContinue = () => {
    // Track continue button click
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_CANCELLATION_MODAL_CONTINUE_CLICKED, {
      user_name: userName,
      is_even_user: isEvenUser,
      is_addon_selected: isPlacementAddonSelected,
      is_one_time: isOneTime,
      discounted_price: isEvenUser ? discountedPriceEvenUser : discountedPriceOneRupee,
      remaining_timer_seconds: timeLeft,
      timestamp: new Date().toISOString()
    })

    if (isEvenUser) {
      razorpayHandlerEvenUser(() => { })
    }
    else {
      razorpayHandler(() => { })
    }
    closeModal();
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => null}
      as="div"
      className="fixed inset-0 z-[999999999999] flex md:justify-start md:mt-3 justify-start flex-col items-center"
    >
      <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-[10px] z-10" />
      <Dialog.Title />
      <Dialog.Description />

      <div className="z-50 m-auto w-[95%] md:w-[30%] flex items-center justify-center p-0">
        <div className="relative overflow-hidden"
          style={{
            background: `radial-gradient(circle, rgba(0, 0, 0, 0) 0%, rgba(0, 4, 7, 0.7) 100%), #013055`,
            borderRadius: '24px',
            border: '0.3px solid #858585',
            boxShadow: '0px 4px 4px 5px rgba(0,0,0,0.25)'
          }}
        >

          {/* Header with close buttons and phone mockup */}
          <div className="flex items-center  justify-center px-3 pt-3 relative">

            {/* Phone mockup section - exact positioning from Figma */}
            <img src="https://img.learntube.ai/public-assets/personalise/Frame%20427322354.webp" alt="" className='w-[70%] md:w-[40%]' />


          </div>
          <button
            onClick={closeModal}
            className="w-[25px] h-[25px] flex items-center justify-center text-white hover:text-gray-300 transition-colors z-10 absolute top-3 right-3"
          >
            <FontAwesomeIcon icon={faXmark} className="text-sm" />
          </button>

          {/* Content section */}
          <div className="px-3 flex flex-col items-center">
            {/* Title and subtitle */}
            <div className=" w-[60%] text-center mb-6 w-full">
              <h2 className="text-white text-[18px] font-bold leading-[1.3] capitalize mb-1  mx-auto">
                {userName}, your personalised course is ready
              </h2>
              <p className="text-[rgba(255,255,255,0.5)] text-[12px] font-normal  mx-auto">
                Curated basis your goal, skills, and experience.
              </p>
            </div>

            {/* Features section */}
            <div className="w-full mb-4">
              <div className="w-full rounded-[12px] mb-4">
                <div className="w-full">
                  <div className="w-full bg-[rgba(64,106,255,0.15)] rounded-[8px] p-2 mb-2  ">
                    <p className="text-[#70aeff] text-[11px] text-center capitalize">
                      <span className="leading-[1.4]">Built to help you achieve your goal </span>
                      <span className="font-bold leading-[1.4]">3x faster:</span>
                    </p>
                  </div>

                  {/* Feature list */}
                  <div className="mt-4 flex flex-col gap-1">
                    {[
                      'Know exactly what to learn next',
                      'Learn from 3,174+ curated courses',
                      'Build skills through real projects',
                      'Earn industry recognised certificate on completion'
                    ].map((feature, index) => (
                      <div key={index} className="flex justify-center items-center rounded-[8px]">
                        <div className="mr-2 flex-shrink-0">

                          <FontAwesomeIcon icon={faCheck} size='sm' className="text-[#54B226]" />

                        </div>
                        <p className="text-[rgba(255,255,255,0.7)] text-[11px] font-normal leading-[1.4] text-center whitespace-nowrap">
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Timer section */}
              <div className="border border-dashed border-[#ff4646] rounded-[4px] px-3 py-[6px] flex items-center justify-center gap-[10px] w-full">
                <p className="text-white text-[12px] font-medium leading-none">Course Is Saved For:</p>
                <p className="text-[#ff4646] text-[11.025px] font-extrabold leading-normal">{formatTime(timeLeft)}</p>
              </div>
            </div>
          </div>

          {/* Bottom CTA section */}
          <div className=" bg-[#013965] px-3 py-3 drop-shadow-[0px_-4px_4px_rgba(0,0,0,0.25)] ">
            <div className="space-y-3">
              {/* Main CTA button */}
              <button
                onClick={handleContinue}
                className="w-full bg-[#7fc241] hover:bg-[#6da835] transition-colors rounded-[12px] h-[52px] flex flex-col items-center justify-center gap-[4px] py-3 px-3 border-b-2 border-l-2 border-r-2 border-[#8bd845]"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[#121212] font-semibold text-[16px] capitalize leading-none">Continue to unlock Course<FontAwesomeIcon icon={faArrowRight} className="text-[#121212] ml-1" size='sm' /></span>
                </div>
                {
                  isEvenUser ? (
                    isPlacementAddonSelected || isProAddonSelected ?

                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0" data-node-id="1641:11001">
                        <div className="flex flex-col font-['Inter:Semi_Bold',sans-serif] font-semibold justify-center items-center gap-1 leading-[0] not-italic relative shrink-0 text-[#121212] text-[14px] text-center whitespace-nowrap" data-node-id="1641:11002">

                          {isPlacementAddonSelected || isProAddonSelected ? <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceEvenUser}</span></span> : <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceEvenUser}{!isOneTime && '/mo'}</span></span>}
                        </div>
                      </div>



                      :

                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0" data-node-id="1641:11001">
                        <div className="flex flex-col font-['Inter:Semi_Bold',sans-serif] font-semibold justify-center items-center gap-1 leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center whitespace-nowrap" data-node-id="1641:11002">

                          {isPlacementAddonSelected || isProAddonSelected ? <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceEvenUser}</span></span> : <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceEvenUser}{!isOneTime && '/mo'}</span></span>}
                        </div>
                      </div>
                  ) : (


                    isPlacementAddonSelected || isProAddonSelected ?

                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0" data-node-id="1641:11001">
                        <div className="flex flex-col font-['Inter:Semi_Bold',sans-serif] font-semibold justify-center items-center gap-1 leading-[0] not-italic relative shrink-0 text-[#121212] text-[14px] text-center whitespace-nowrap" data-node-id="1641:11002">
                          {isPlacementAddonSelected || isProAddonSelected ? <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceOddUser}</span></span> : <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹299/mo</span></span>}
                        </div>
                      </div>



                      :

                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0" data-node-id="1641:11001">
                        <div className="flex flex-col font-['Inter:Semi_Bold',sans-serif] font-semibold justify-center items-center gap-1 leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center whitespace-nowrap" data-node-id="1641:11002">
                          <span className='mt-1.5 text-[10px] text-[#121212] font-light'> <span>Pay Just:</span> <span className='font-semibold'>₹{discountedPriceOddUser}/month</span></span>
                        </div>
                      </div>


                  )

                }
              </button>

              {/* Bottom text */}
              <p className="text-[rgba(255,255,255,0.5)] text-[12px] font-bold text-center leading-none w-full">
                <span className="font-normal">Unlimited access</span>
                <span className="font-bold"> • </span>
                <span className="font-normal">Cancel anytime</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default CancellationModal